import { GridColDef, GridCellParams } from '@mui/x-data-grid-pro';
import { BasicDataGrid } from '../Table/BasicDataGrid';
import { getFormattedDate } from '../../../utils/helpers';
import { DEFAULT_MINI_TABLE_SX_STYLES } from '../../../utils/styleHelpers';
import { McpAccessProfile, McpAccessScope } from '../../../store/McpAccess';
import { McpAccessActionsCell } from './McpAccessActionsCell';
import { SCOPE_LABELS } from './mcpAccessLabels';

interface ColumnHandlers {
  actionsDisabled: boolean;
  allowedScopes: McpAccessScope[];
  onRotate: (profile: McpAccessProfile) => void;
  onEdit: (profile: McpAccessProfile) => void;
  onRevoke: (profile: McpAccessProfile) => void;
}

export const getMcpAccessColumns = ({
  actionsDisabled,
  allowedScopes,
  onRotate,
  onEdit,
  onRevoke
}: ColumnHandlers): GridColDef[] => [
  { field: 'title', headerName: 'Title', flex: 1, minWidth: 160, sortable: false },
  {
    field: 'scope',
    headerName: 'Scope',
    width: 130,
    sortable: false,
    renderCell: (params: GridCellParams) => SCOPE_LABELS[params.row.scope as McpAccessScope]
  },
  {
    field: 'createdAt',
    headerName: 'Created',
    width: 130,
    sortable: false,
    renderCell: (params: GridCellParams) => getFormattedDate(params.value as string, 'MMM D, YYYY')
  },
  {
    field: 'expiresAt',
    headerName: 'Expires',
    width: 130,
    sortable: false,
    renderCell: (params: GridCellParams) => getFormattedDate(params.value as string, 'MMM D, YYYY')
  },
  {
    field: 'lastUsedAt',
    headerName: 'Last used',
    width: 180,
    sortable: false,
    renderCell: (params: GridCellParams) =>
      getFormattedDate(params.value as string | null, 'MMM D, YYYY h:mm A', 'Never')
  },
  {
    field: 'actions',
    headerName: 'Actions',
    width: 150,
    sortable: false,
    filterable: false,
    align: 'right',
    headerAlign: 'right',
    renderCell: (params: GridCellParams) => {
      const profile = params.row as McpAccessProfile;
      return (
        <McpAccessActionsCell
          profile={profile}
          disabled={actionsDisabled}
          manageDisabled={!allowedScopes.includes(profile.scope)}
          onRotate={onRotate}
          onEdit={onEdit}
          onRevoke={onRevoke}
        />
      );
    }
  }
];

const EmptyOverlay = () => (
  <div
    data-testid="mcp-access-empty"
    className="flex items-center justify-center h-full text-grey-6">
    You have no MCP access tokens yet.
  </div>
);

const ErrorOverlay = () => (
  <div
    data-testid="mcp-access-error"
    className="flex items-center justify-center h-full text-grey-6">
    We couldn&apos;t load your MCP access tokens.
  </div>
);

interface McpAccessProfilesTableProps {
  profiles: McpAccessProfile[];
  allowedScopes: McpAccessScope[];
  loading?: boolean;
  hasError?: boolean;
  actionsDisabled?: boolean;
  onRotate: (profile: McpAccessProfile) => void;
  onEdit: (profile: McpAccessProfile) => void;
  onRevoke: (profile: McpAccessProfile) => void;
}

export const McpAccessProfilesTable = ({
  profiles,
  allowedScopes,
  loading = false,
  hasError = false,
  actionsDisabled = false,
  onRotate,
  onEdit,
  onRevoke
}: McpAccessProfilesTableProps) => (
  <BasicDataGrid
    className="flex"
    dataTestId="mcp-access-table"
    sxStyle={DEFAULT_MINI_TABLE_SX_STYLES}
    rows={profiles}
    columns={getMcpAccessColumns({ actionsDisabled, allowedScopes, onRotate, onEdit, onRevoke })}
    primaryKey="id"
    localLoading={loading}
    slots={{ noRowsOverlay: hasError ? ErrorOverlay : EmptyOverlay }}
  />
);
